<script lang="ts">
    import ColorThumbs from '@tile/components/color-thumbs/ColorThumbs.svelte';
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import JsonLd from '@tile/components/json-ld/JsonLd.svelte';
    import ProductComparisonCheckBox from '@tile/components/product-comparison/ProductComparisonCheckBox.svelte';
    import DealBadge from '@tile/components/product-image/DealBadge.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import ProductLink from '@tile/components/product-title/ProductLink.svelte';
    import Title from '@tile/components/product-title/Title.svelte';
    import RadioEquipmentRegulation from '@tile/components/radio-equipment-regulation/RadioEquipmentRegulation.svelte';
    import Rating from '@tile/components/rating/Rating.svelte';
    import SocialProofBadges from '@tile/components/social-proof-badges/SocialProofBadges.svelte';
    import SustainabilityBadges from '@tile/components/sustainability-badges/SustainabilityBadges.svelte';
    import TopInfo from '@tile/components/top-info/TopInfo.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { DefaultLayout } from '@tilelist/layouts/default-layout/DefaultLayout.ts';
    import DeliveryDispatcher from '@tilelist/layouts/dispatchers/delivery-dispatcher/DeliveryDispatcher.svelte';
    import PriceDispatcher from '@tilelist/layouts/dispatchers/price-dispatcher/PriceDispatcher.svelte';
    import ProductImageDispatcher from '@tilelist/layouts/dispatchers/product-image-dispatcher/ProductImageDispatcher.svelte';
    import type { TileLayoutProps } from '../shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        layout: DefaultLayout;
        aspectRatio: ImageAspectRatio;
        showInstallments: boolean;
        bestVariationBenefit?: string;
    };

    let {
        advertiserLegalName,
        aspectRatio,
        colors,
        element = $bindable(null),
        funderLegalName,
        imageLoadingOptions,
        onColorChange,
        onRedirectToPdp,
        rule,
        showInstallments,
        sponsored,
        layout,
        bestVariationBenefit,
    }: Props = $props();

    const onex = injectWiring().onex;
    const forceIndex = $derived(onex.isToggleActive('REPTILE_1495_FORCE_INDEX'));
    const { variation, title, link: rawLink } = $derived(layout);
    const link = $derived(forceIndex ? { ...rawLink, index: true } : rawLink);
</script>

<ProductComparisonCheckBox currentVariation={variation} />

<div data-jarvis="reptile-tile-container" data-variation-id={variation.variationId} bind:this={element}>
    <div class="reptile-tile__image">
        <ProductImageDispatcher
            images={layout.images}
            fallbackImage={layout.primaryImage}
            variationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            brand={variation.brand || ''}
            title={variation.title?.withoutBrand || variation.title?.full || ''}
            {imageLoadingOptions}
            onClick={onRedirectToPdp}
            {aspectRatio}
        />
        {#if variation.deal}
            <DealBadge deal={variation.deal} variationId={variation.variationId} {rule} />
        {/if}
        {#if variation.socialProof && Object.values(variation.socialProof).some(Boolean)}
            <div class="reptile-tile__social-proof-badges">
                <SocialProofBadges socialProof={variation.socialProof} />
            </div>
        {/if}
    </div>
    <div class="reptile-tile__content">
        <header class="reptile-tile__header">
            {#if sponsored || false}<ofc-sponsored-tag-v1
                    funder-legal-name={funderLegalName}
                    advertiser-legal-name={advertiserLegalName}
                ></ofc-sponsored-tag-v1>{/if}
            <ProductLink {link} onClick={onRedirectToPdp}>
                <Title {title} />
            </ProductLink>

            <TopInfo infos={variation.topInfos} />
        </header>
        <EekLabel labels={variation?.energyLabels || []} />
        <RadioEquipmentRegulation radioEquipmentRegulation={variation.radioEquipmentRegulation} />
        <Rating customerReviews={variation.customerReviews} />
        <PriceDispatcher
            variationId={variation.variationId}
            saleTags={variation?.saleTags}
            price={variation.price}
            dealHighlight={variation.deal?.highlight}
            {showInstallments}
            {rule}
            {bestVariationBenefit}
        />
        <SustainabilityBadges badges={variation.sustainabilityBadges} />
        <DeliveryDispatcher availability={variation.availability} ottoUp={variation.ottoUp} />
        <ColorThumbs
            {colors}
            currentVariationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            productLinkTitle={variation.title?.full || ''}
            onClickSurplusColors={onRedirectToPdp}
            {onColorChange}
        />
    </div>
    <JsonLd {variation} />
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-tile {
        &__content,
        &__header {
            display: flex;
            flex-direction: column;
        }

        &__image {
            position: relative;
        }

        &__social-proof-badges {
            position: absolute;
            bottom: 0;
            left: 0;

            margin-right: 10px;
            margin-bottom: tokens.$oc-semantic-spacing-25;
            margin-left: 10px;
        }

        &__content {
            row-gap: tokens.$oc-semantic-spacing-content-layout-gap;
            padding: tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-75;

            @include mixins.breakpoint-from-l {
                padding: tokens.$oc-semantic-spacing-100;
            }
        }

        &__header {
            row-gap: tokens.$oc-semantic-spacing-default-between-blocks;
        }
    }
</style>
